import { Controller } from "@hotwired/stimulus"

const DIALOG_HIDDEN_CLASSES = ["backdrop:opacity-0", "opacity-0"]
const DIALOG_OPEN_CLASSES = ["backdrop:opacity-100", "opacity-100"]

export default class extends Controller {
  static targets = ["dialog"]
  static values = {
    onClose: { default: [], type: Array },
    onOpen: { default: [], type: Array },
  }

  close = (e) => {
    e?.preventDefault()
    this.closeDialog()
  }

  closeDialog = () => {
    this.withDialog((dialog) => {
      this.resetClasses(dialog)

      setTimeout(() => {
        if (this.isDialogAvailable(dialog) && dialog.open) {
          dialog.close()
        }
      }, 500)
    })
  }

  open = (e) => {
    e?.preventDefault()

    this.withDialog((dialog) => {
      document.body.classList.add("overflow-hidden")

      try {
        dialog.showModal()
      } catch {
        document.body.classList.remove("overflow-hidden")
        return
      }

      requestAnimationFrame(() => {
        this.setDialogClasses(dialog, true)
      })

      this.handleRemoteCallbacks(this.onOpenValue)
    })
  }

  // For non-dismissable dialogs: wire to the dialog element's cancel event
  // to swallow Esc, which would otherwise close a shown modal.
  preventClose = (e) => {
    e?.preventDefault()
  }

  withDialog(callback) {
    const dialog = this.getDialogElement()
    if (!this.isDialogAvailable(dialog)) return

    callback?.(dialog)
  }

  isDialogAvailable(dialog) {
    return dialog instanceof HTMLDialogElement && dialog.isConnected
  }

  getDialogElement() {
    if (!this.element) return null

    if (this.element.matches('[data-dialog-target="dialog"]')) {
      return this.element
    }

    return this.element.querySelector('[data-dialog-target="dialog"]')
  }

  setDialogClasses = (dialog, isOpen) => {
    const addClasses = isOpen ? DIALOG_OPEN_CLASSES : DIALOG_HIDDEN_CLASSES
    const removeClasses = isOpen ? DIALOG_HIDDEN_CLASSES : DIALOG_OPEN_CLASSES

    dialog.classList.remove(...removeClasses)
    dialog.classList.add(...addClasses)
  }

  resetClasses = (dialog) => {
    document.body.classList.remove("overflow-hidden")
    this.setDialogClasses(dialog, false)
  }

  handleDialogClose = (event) => {
    const dialog = event?.target
    if (!(dialog instanceof HTMLDialogElement)) return

    this.resetClasses(dialog)
    this.handleRemoteCallbacks(this.onCloseValue)
  }

  connect() {
    this.withDialog((dialog) => {
      this.boundDialogCloseHandler = this.handleDialogClose
      dialog.addEventListener("close", this.boundDialogCloseHandler)

      if (dialog.hasAttribute("data-auto-open")) {
        this.open()
      }
    })
  }

  disconnect() {
    const dialog = this.getDialogElement()
    if (!dialog) return

    if (this.boundDialogCloseHandler) {
      dialog.removeEventListener("close", this.boundDialogCloseHandler)
      this.boundDialogCloseHandler = null
    }

    this.resetClasses(dialog)

    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.close()
    }
  }

  handleRemoteCallbacks = (callbacks) => {
    if (!Array.isArray(callbacks)) return

    callbacks.forEach((callback) => {
      if (typeof callback !== "string") return

      const callbackParts = callback.split("#")
      const controllerName = callbackParts[0]
      const actionName = callbackParts[1]
      const selector = callbackParts.slice(2).join("#")

      if (!controllerName || !actionName) return

      const querySelector = selector || `[data-controller*="${controllerName}"]`
      const targetElement = document.querySelector(querySelector)

      if (!targetElement) return

      const targetController = this.application.getControllerForElementAndIdentifier(
        targetElement,
        controllerName
      )

      if (!targetController || typeof targetController[actionName] !== "function") {
        return
      }

      targetController[actionName]()
    })
  }
}
